import { toast } from "sonner";
import { Fragment, useEffect, useState } from "react";
import { format, fromUnixTime } from "date-fns";

import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { usePostHog } from "@posthog/react";
import { Sparkle, DotsThreeVertical } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Credits } from "@/components/card/credits";
import { EmptyIcon } from "@/components/icons/empty";
import { PageHeader } from "@/components/layout/heading";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CreditsInfo } from "@/components/info/credits";
import { SubscriptionCancellationModal } from "@/components/subscription/cancellation";

import { usePromptCredit } from "@/hooks/use-prompt-credit";
import { useSession, useSessionUser } from "@/hooks/use-session";
import { useSubscriptionStore } from "@/store/subscription";
import { SubscriptionApiFactory } from "@/apis/subscription";
import { mStripeCallbackURL } from "@/config/stripe";

import { Plan } from "@/schema/subscription";
import { mPlans } from "@/constants/subscription-plans";
import { unwrapApiError } from "@/lib/error";
import { onPlanUpgradeViewEvent } from "@/events/subscription";

export const Route = createFileRoute("/dashboard/settings/_layout/subscription")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="min-h-full w-full @container">
      <PageHeader title="Plans & Credits" description="Manage your subscription and purchase additional credits" className="mb-8 hidden lg:block" />
      <PlansSettings />
    </div>
  );
}

function PlansSettings() {
  const posthog = usePostHog();
  const subscription = useSubscriptionStore((state) => state.subscription);

  const { data: plans = [], status } = useQuery({
    queryKey: SubscriptionApiFactory.Keys.FetchAllPlans(),
    queryFn: () => SubscriptionApiFactory.Apis.FetchAllPlans(),
    select: (data) => {
      const proPlanIds = ["bacon-pro", "bacon-pro-yearly"];
      const hasProPlan = subscription?.base_subscriptions.some((sub) => proPlanIds.includes(sub.plan_id) || sub.plan_id === "try-it-out-7d-plan-id") ?? false;
      if (hasProPlan) return data.filter((plan) => proPlanIds.includes(plan.id)).sort((a, b) => (a.price || 0) - (b.price || 0));
      return data.filter((plan) => !proPlanIds.includes(plan.id) && mPlans.find((p) => p.id === plan.id)?.visible).sort((a, b) => (a.price || 0) - (b.price || 0));
    },
  });

  useEffect(() => {
    if (status === "success") {
      onPlanUpgradeViewEvent({}, { posthog });
    }
  }, [status, posthog]);

  switch (status) {
    case "pending":
      return (
        <div className="min-h-full w-full @container space-y-6">
          <Card className="border-none lg:border py-0 lg:py-6">
            <CardContent className="px-0 lg:px-6">
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Array.from({ length: 2 }).map((_, index) => (
              <Card key={index}>
                <CardContent className="p-6 space-y-6">
                  <Skeleton className="h-8 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-12 w-24" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-32 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );

    case "error":
      return (
        <div className="min-h-full w-full @container space-y-6">
          <Card className="border-none lg:border py-0 lg:py-6">
            <CardContent className="p-0 lg:p-12">
              <div className="flex flex-col items-center justify-center text-center">
                <EmptyIcon className="size-16 text-muted-foreground" />
                <p className="text-lg font-medium text-foreground mt-4">Error loading plans</p>
                <p className="text-sm text-muted-foreground mt-1">Please try again later.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );

    default:
      return (
        <div className="min-h-full w-full @container space-y-6">
          <Credits />
          {plans.length ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
            </div>
          ) : (
            <Card className="border-none lg:border py-0 lg:py-6">
              <CardContent className="p-0 lg:p-12">
                <div className="flex flex-col items-center justify-center text-center">
                  <EmptyIcon className="size-16 text-muted-foreground" />
                  <p className="text-lg font-medium text-foreground mt-4">No plans available</p>
                  <p className="text-sm text-muted-foreground mt-1">Please check back later.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      );
  }
}

function PlanCard({ plan }: { plan: Plan }) {
  const [subscription] = usePromptCredit();
  const [isCancellationModalOpen, setCancellationModalOpen] = useState(false);

  const features = mPlans.find((p) => p.id === plan.id)?.features || [];

  const activeBaseSubscription = subscription.base_subscriptions[0];
  const isActiveBaseSubscription = activeBaseSubscription.is_active_base_subscription && (activeBaseSubscription.change_to_plan_id === plan.id || activeBaseSubscription.plan_id === plan.id);

  return (
    <Fragment>
      <Card className="group">
        <CardHeader className="gap-1.5">
          <div className="flex items-center justify-between gap-x-2">
            <CardTitle className="capitalize">{plan.name}</CardTitle>
            {isActiveBaseSubscription && !activeBaseSubscription.cancel_at_period_end ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <DotsThreeVertical weight="bold" className="size-5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" alignOffset={12}>
                  <DropdownMenuItem onClick={() => setCancellationModalOpen(true)} className="text-destructive">
                    Cancel Subscription
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
          <CardDescription>{plan.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <PlanHeader plan={plan} />
          <PlanButton plan={plan} />
          <PlanCredits plan={plan} />
          <div className="space-y-3 pt-2">
            <p className="text-sm font-medium text-foreground">Everything included in this plan</p>
            <ul className="list-disc text-sm text-muted-foreground space-y-2 pl-3">
              {features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
      <SubscriptionCancellationModal open={isCancellationModalOpen} onOpenChange={setCancellationModalOpen} />
    </Fragment>
  );
}

function PlanButton({ plan }: { plan: Plan }) {
  const user = useSessionUser();
  const [subscription] = usePromptCredit();

  const setSubscription = useSubscriptionStore((state) => state.setSubscription);

  const { mutateAsync: updateSubscription, isPending: isUpdatingSubscriptionPending } = useMutation({
    mutationFn: (plan_id: string) => SubscriptionApiFactory.Apis.UpdateSubscription(plan_id),
  });

  const { mutate: cancelSubscription, isPending: isCancellingSubscriptionPending } = useMutation({
    mutationFn: SubscriptionApiFactory.Apis.CancelSubscription,
  });

  const { mutateAsync: createSubscription, isPending: isCreatingSubscriptionPending } = useMutation({
    mutationFn: SubscriptionApiFactory.Apis.CreateSubscription,
  });

  const activeBaseSubscription = subscription.base_subscriptions[0];
  const endDateEpochInSeconds = activeBaseSubscription.end_date;
  const currentDateEpochInSeconds = Date.now() / 1000;

  const handleCreateSubscription = async () => {
    try {
      const response = await createSubscription({
        email: user.email,
        plan_id: plan.id,
        callback_url: mStripeCallbackURL,
        failure_callback_url: mStripeCallbackURL,
        with_trial: false,
        ui_mode: "hosted",
      });

      if (response.redirect_url) {
        window.location.href = response.redirect_url;
      } else {
        toast.error("Failed to create subscription", {
          description: "No redirect URL received from server",
        });
      }
    } catch (error) {
      toast.error("Failed to create subscription", {
        description: unwrapApiError(error),
      });
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      await updateSubscription(plan.id);
      toast.success("Subscription updated successfully");
      const response = await SubscriptionApiFactory.Apis.FetchUserSubscription();
      setSubscription(response.data);

      setTimeout(async () => {
        const response = await SubscriptionApiFactory.Apis.FetchUserSubscription();
        setSubscription(response.data);
      }, 3000);
    } catch (error) {
      toast.error("Failed to update subscription", {
        description: unwrapApiError(error),
      });
    }
  };

  const handleRenewSubscription = async () => {
    cancelSubscription(
      { user_id: user.id, cancel_at_period_end: false },
      {
        onSuccess: async () => {
          toast.success("Subscription cancelled successfully");
          try {
            const response = await SubscriptionApiFactory.Apis.FetchUserSubscription();
            setSubscription(response.data);

            setTimeout(() => {
              SubscriptionApiFactory.Apis.FetchUserSubscription()
                .then((response) => setSubscription(response.data))
                .catch((error) => {
                  toast.error("Failed to fetch subscription", { description: unwrapApiError(error) });
                });
            }, 3000);
          } catch (error) {
            toast.error("Failed to fetch subscription", { description: unwrapApiError(error) });
          }
        },
        onError: (error) => {
          toast.error("Failed to cancel subscription", { description: unwrapApiError(error), className: "capitalize" });
        },
      }
    );
  };

  if (activeBaseSubscription.cancel_at_period_end && activeBaseSubscription.plan_id === plan.id) {
    return (
      <div className="space-y-2">
        <Button disabled={isCancellingSubscriptionPending} onClick={handleRenewSubscription} className="w-full">
          {isCancellingSubscriptionPending ? <div className="size-4 spinner" /> : null}
          {isCancellingSubscriptionPending ? "Loading..." : "Keep Subscription"}
        </Button>
        <p className="text-sm text-muted-foreground text-center">Your subscription ends on {endDateEpochInSeconds ? format(fromUnixTime(endDateEpochInSeconds), "PPP") : "—"}.</p>
      </div>
    );
  }

  if (endDateEpochInSeconds && endDateEpochInSeconds < currentDateEpochInSeconds) {
    return (
      <Button disabled={isCreatingSubscriptionPending} onClick={handleCreateSubscription} className="w-full">
        {isCreatingSubscriptionPending ? <div className="size-4 spinner" /> : null}
        {isCreatingSubscriptionPending ? "Loading..." : "Renew Plan"}
      </Button>
    );
  }

  if (activeBaseSubscription.change_to_plan_id === plan.id) {
    return (
      <Button disabled className="w-full">
        Upcoming Plan
      </Button>
    );
  }

  if (activeBaseSubscription.is_active_base_subscription && activeBaseSubscription.plan_id === plan.id) {
    return (
      <Button disabled className="w-full">
        Current Plan
      </Button>
    );
  }

  if (activeBaseSubscription.plan_id === "free-plan") {
    return (
      <Button disabled={isCreatingSubscriptionPending} onClick={handleCreateSubscription} className="w-full">
        {isCreatingSubscriptionPending ? <div className="size-4 spinner" /> : null}
        {isCreatingSubscriptionPending ? "Loading..." : "Upgrade Plan"}
      </Button>
    );
  }

  return (
    <Button disabled={isUpdatingSubscriptionPending} onClick={handleUpdateSubscription} className="w-full">
      {isUpdatingSubscriptionPending ? <div className="size-4 spinner" /> : null}
      {isUpdatingSubscriptionPending ? "Loading..." : "Change Plan"}
    </Button>
  );
}

function PlanHeader({ plan }: { plan: Plan }) {
  const {
    profile: { customer },
  } = useSession();
  const subscription = useSubscriptionStore((state) => state.subscription);

  if (customer.retention_coupon_availed && plan.id === subscription?.base_subscriptions[0].plan_id) {
    return (
      <h3 className="flex items-baseline gap-x-1.5">
        <span className="text-xl font-medium text-muted-foreground line-through">
          {Intl.NumberFormat("en-US", { style: "currency", currency: plan.currency, compactDisplay: "short", minimumFractionDigits: 0 }).format(Number(plan.price))}
        </span>
        <span className="text-5xl font-semibold text-foreground">
          {Intl.NumberFormat("en-US", { style: "currency", currency: plan.currency, compactDisplay: "short", minimumFractionDigits: 0 }).format(Number(plan.price ? plan.price / 2 : 0))}
        </span>
        <p className="text-muted-foreground">for three months</p>
      </h3>
    );
  }

  return (
    <div className="flex items-baseline gap-2">
      <span className="text-5xl font-bold text-foreground">{Intl.NumberFormat("en-US", { style: "currency", currency: plan.currency, compactDisplay: "short" }).format(Number(plan.price))}</span>
      {plan.billing_cycle_days ? (
        <span className="text-muted-foreground">per {plan.billing_cycle_days === 30 ? "month" : plan.billing_cycle_days === 365 ? "year" : `${plan.billing_cycle_days} days`}</span>
      ) : null}
    </div>
  );
}

function PlanCredits({ plan }: { plan: Plan }) {
  const _plan = mPlans.find((p) => p.id === plan.id);
  if (!_plan) {
    return null;
  }

  return (
    <div className="flex items-start gap-x-1.5">
      <Sparkle className="size-4 shrink-0 translate-y-1" />
      <div className="space-y-2">
        <div className="flex items-center gap-x-1.5">
          <h5 className="text-base text-foreground">
            <span className="font-medium">{_plan.credits}&nbsp;</span>credits per month
          </h5>
          <CreditsInfo />
        </div>
        <div className="space-y-2">
          {_plan.creativesBreakdown.map((credit, index) => (
            <p key={index} className="text-sm text-muted-foreground">
              =&nbsp;<span className="font-medium">{credit}</span>
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}
